import { LngLatLike, LngLatBounds, Marker } from 'mapbox-gl';
import { Feature, FeatureCollection, Geometry, LineString, Position } from '@turf/helpers';
import turfAlong from '@turf/along';
import turfBearing from '@turf/bearing';
import { HEADER_SIZE_HEIGHT_DESKTOP, SPACE_40 } from '@alltrails/shared/denali/tokens';
import { MapboxMapShim } from 'types/Map';
import { hideAtMap, showAtMap, setBoundsFromAtMapGeojson, DEFAULT_AT_MAP_PADDING } from 'utils/mapbox/overlays/at_map';
import { fitMapToBounds } from 'utils/mapbox/map_helpers';
import { mapMarkerDiv } from 'utils/mapbox/layers/markers';
import { addPolylines, removePolylines } from 'utils/mapbox/overlays/polylines';
import FlyoverMapEventHandlers from 'types/FlyoverMapEventHandlers';

export const FLYOVER_LINE = 'flyover-line';
export const FLYOVER_ANIM_DURATION_MS = 800;
const DEFAULT_CONTROLS_PADDING = SPACE_40;

/**
 * Hides part of the original polyline.
 * Initializes the mapbox map to draw the animated line for flyover by adding a new polyline layer & marker to animate.
 * @param map [Mapbox map object](https://docs.mapbox.com/mapbox-gl-js/api/map/#map)
 * @param atMapGeojson a FeatureCollection of the map to animate - typically should match the original polyline
 * @returns a [Mapbox Marker](https://docs.mapbox.com/mapbox-gl-js/api/markers/#marker) to animate
 */
export function initializeFlyoverMap(
  map: MapboxMapShim,
  atMapGeojson: FeatureCollection<
    Geometry,
    {
      [name: string]: any;
    }
  >
): { marker: Marker } {
  hideAtMap(map);

  // Add a line feature and layer. This feature will get updated as we progress the animation
  addPolylines(
    map,
    FLYOVER_LINE,
    atMapGeojson,
    { lineMetrics: true } /* sourceOpts */,
    { 'line-color': 'rgba(0,0,0,0)' } /* opts */,
    null /* addUnderLayerTypes */,
    false /* isMapCreatorPage */
  );

  const marker = new Marker(mapMarkerDiv('current'));

  return { marker };
}

/**
 * Removes flyover lines & markers from the map & shows the original polyline.
 * Resets map bounds to normal.
 * @param map [Mapbox map object](https://docs.mapbox.com/mapbox-gl-js/api/map/#map)
 * @param atMapGeojson a FeatureCollection of the original atMap - typically should match the original polyline
 */
export function exitFlyoverMap(
  map: MapboxMapShim,
  atMapGeojson: FeatureCollection<
    Geometry,
    {
      [name: string]: any;
    }
  >
): void {
  removePolylines(map, FLYOVER_LINE);
  const bounds = new LngLatBounds();
  setBoundsFromAtMapGeojson(atMapGeojson, bounds);
  fitMapToBounds(map, bounds, DEFAULT_AT_MAP_PADDING, 1000 /* duration */, false /* maintainZoomLevel */, true /* zoomOutByDefault */);
  showAtMap(map);
}

/**
 * Given pathing information, calculates the position along the path along with the bearing of it relative to another point.
 * @param path line string representing a path in which to calculate position values from
 * @param pathDistance the total distance of the path
 * @param percentageComplete a value from 0 to 1 indicating a percentage along the path
 * @param previousPosition a separate position used to calculate bearing
 * @returns {object} an object with a `bearing`, `lngLat` of the position, and a position
 */
export function computePositionValues(
  path: Feature<
    LineString,
    {
      [name: string]: any;
    }
  >,
  pathDistance: number,
  percentageComplete: number,
  previousPosition: Position | null
): {
  bearing: number | null;
  lngLat: LngLatLike;
  position: Position;
} {
  // Get the new latitude and longitude by sampling along the path
  const position = turfAlong(path, pathDistance * percentageComplete).geometry.coordinates;
  const lngLat = {
    lng: position[0],
    lat: position[1]
  };

  const bearing = previousPosition ? turfBearing(previousPosition, position) : null;

  return {
    bearing,
    lngLat,
    position
  };
}

/**
 * Adds event listeners on the map/document to handle pause/play interactions.
 * @param map [Mapbox map object](https://docs.mapbox.com/mapbox-gl-js/api/map/#map)
 * @param pause a function to pause the flyover
 * @param {FlyoverMapEventHandlers} eventHandlers object of event handlers
 */
export function addFlyoverEventListeners(map: MapboxMapShim, pause: () => void, eventHandlers?: FlyoverMapEventHandlers) {
  if (!eventHandlers) {
    return;
  }

  map.on('dblclick', pause);

  const { onKeyDown, onMouseDown, onMouseMove, onMouseUp, onTouchStart, onTouchEnd, onWheel } = eventHandlers;
  map.on('mousedown', onMouseDown);
  map.on('mousemove', onMouseMove);
  map.on('mouseup', onMouseUp);
  map.on('wheel', onWheel);

  map.on('touchstart', onTouchStart);
  map.on('touchend', onTouchEnd);

  document.addEventListener('keydown', onKeyDown);
}

/**
 * Removes event listeners on the map/document added from `addFlyoverEventListeners`.
 * @param map [Mapbox map object](https://docs.mapbox.com/mapbox-gl-js/api/map/#map)
 * @param pause a function to pause the flyover
 * @param {FlyoverMapEventHandlers} eventHandlers object of event handlers
 */
export function removeFlyoverEventListeners(map: MapboxMapShim, pause: () => void, eventHandlers?: FlyoverMapEventHandlers) {
  if (!eventHandlers) {
    return;
  }

  // pause events
  map.off('dblclick', pause);

  const { onKeyDown, onMouseDown, onMouseMove, onMouseUp, onTouchStart, onTouchEnd, onWheel } = eventHandlers;
  map.off('mousedown', onMouseDown);
  map.off('mousemove', onMouseMove);
  map.off('mouseup', onMouseUp);
  map.off('wheel', onWheel);

  map.off('touchstart', onTouchStart);
  map.off('touchend', onTouchEnd);

  document.removeEventListener('keydown', onKeyDown);
}

/**
 * Gets a padding value for the bottom controls based on the map size vs. the bottom of the screen, used to emulate a `sticky` anchored to the bottom.
 * Used to assure the bottom-achored flyover controls are never overlapped by a mobile broswer's url bar.
 * @param map [Mapbox map object](https://docs.mapbox.com/mapbox-gl-js/api/map/#map)
 * @returns a bottom padding value from the bottom of the map.
 */
export function getControlsPadding(map: MapboxMapShim): number {
  if (typeof window === undefined || !map) {
    return DEFAULT_CONTROLS_PADDING;
  }

  const bottomDepth = window.scrollY + window.innerHeight;
  const mapBottom = parseInt(map.getCanvas().style.height) + HEADER_SIZE_HEIGHT_DESKTOP;
  return mapBottom > bottomDepth ? DEFAULT_CONTROLS_PADDING + mapBottom - bottomDepth : DEFAULT_CONTROLS_PADDING;
}
